/**
 * SectionAccordion Component
 * Reusable collapsible section wrapper for the session detail drawer tabs.
 * Follows the PhaseAccordion pattern with Collapse + UnstyledButton.
 */

import { useState } from 'react';
import { Paper, Group, Text, Collapse, Box } from '@mantine/core';
import { ChevronRight, ChevronDown } from 'react-feather';

interface SectionAccordionProps {
  title: React.ReactNode;
  rightSection?: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

export function SectionAccordion({
  title,
  rightSection,
  defaultExpanded = false,
  children,
}: SectionAccordionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Paper
      radius="md"
      mb="sm"
      style={{ border: '1px solid var(--mantine-color-gray-2)' }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
        style={{ width: '100%', cursor: 'pointer' }}
      >
        <Group
          justify="space-between"
          p="md"
          style={{
            borderBottom: expanded ? '1px solid var(--mantine-color-gray-2)' : 'none',
          }}
        >
          <Group gap="sm">
            {expanded ? (
              <ChevronDown size={16} color="var(--mantine-color-dimmed)" />
            ) : (
              <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
            )}
            <Text fw={600} size="sm">
              {title}
            </Text>
          </Group>
          {rightSection && (
            <div onClick={(e) => e.stopPropagation()}>
              {rightSection}
            </div>
          )}
        </Group>
      </Box>

      <Collapse in={expanded}>
        <div style={{ padding: 'var(--mantine-spacing-md)' }}>
          {children}
        </div>
      </Collapse>
    </Paper>
  );
}

export default SectionAccordion;
